import { store } from 'config/redux'
import { IConfigRetailUser, IConfigStateGroups, Result } from 'constants/interfaces'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { getContextName } from 'hooks/useContextName'
export const { getState } = store

const NO_ANSWER_PLACEHOLDER = '—'

export const exportAnswersToExcel = (
  contextsWithResults: Result[],
  contextsWithoutResults: string[],
  groups: IConfigStateGroups,
  retailUsersObject: { [key: string]: IConfigRetailUser },
  fileName: string
): void => {
  const { mappedGroupIdsByUsers } = getState().config.config

  // Check if any context has groups mapping
  const allContextIds = [...contextsWithResults.map((item) => item.context_id), ...contextsWithoutResults]
  const hasGroupsColumn = allContextIds.some((contextId) => mappedGroupIdsByUsers?.[contextId]?.length > 0)

  // Helper to get group names from group IDs
  const getGroupNames = (contextId: string): string => {
    const groupIds = mappedGroupIdsByUsers?.[contextId] || []
    if (!groupIds || groupIds.length === 0) return ''
    return groupIds
      .map((groupId: string) => groups?.[groupId]?.name || groupId)
      .filter(Boolean)
      .join(', ')
  }

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('worksheet 1')

  // Define columns - add "User's groups" if applicable
  const columns: { header: string; key: string; width: number }[] = [
    { header: 'Store Name', key: 'context_id', width: 50 },
    { header: 'Answer', key: 'answer', width: 100 },
  ]

  if (hasGroupsColumn) {
    columns.push({ header: 'User groups', key: 'user_groups', width: 50 })
  }

  worksheet.columns = columns

  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).alignment = { horizontal: 'center' }
  worksheet.getRow(1).height = 20

  const styleRow = (row: ExcelJS.Row, opts?: { isNoAnswerRow?: boolean }) => {
    row.eachCell((cell, colNumber) => {
      const isAnswerCell = colNumber === 2
      cell.alignment = {
        vertical: 'middle',
        horizontal: opts?.isNoAnswerRow && isAnswerCell ? 'center' : 'left',
        wrapText: true,
      }
      cell.font = {
        size: 12,
        name: 'Arial',
        bold: colNumber === 1,
        ...(opts?.isNoAnswerRow && isAnswerCell ? { color: { argb: 'FF999999' } } : {}),
      }

      if (colNumber === 1) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF1F1F1' },
        }
      }

      cell.border = {
        top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      }
    })
  }

  // 1) Сначала — пользователи/контексты с результатами
  contextsWithResults.forEach((item) => {
    const rowData: { context_id: string; answer: string; user_groups?: string } = {
      context_id: getContextName(item.context_id, groups, retailUsersObject!),
      answer: item.answer?.toString() || '',
    }
    if (hasGroupsColumn) {
      rowData.user_groups = getGroupNames(item.context_id)
    }
    const row = worksheet.addRow(rowData)
    styleRow(row)
  })

  // 2) Потом — пользователи/контексты без ответов
  contextsWithoutResults.forEach((contextId) => {
    const rowData: { context_id: string; answer: string; user_groups?: string } = {
      context_id: getContextName(contextId, groups, retailUsersObject!),
      answer: NO_ANSWER_PLACEHOLDER,
    }
    if (hasGroupsColumn) {
      rowData.user_groups = getGroupNames(contextId)
    }
    const row = worksheet.addRow(rowData)
    styleRow(row, { isNoAnswerRow: true })
  })

  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer]), `${fileName}.xlsx`)
  })
}
